import axios, { Axios, AxiosError, InternalAxiosRequestConfig } from "axios";
import { z } from "zod";
import { API_BASE_URL } from "../config";
import { AutoRenewReviewEntry, ListAutoRenewReviewRequest, ListAutoRenewReviewResponse, autoRenewReviewEntry, listAutoRenewReviewResponse,
    Alert, AlertAuditLogResponse, AlertHistoryResponse, AlertType, AuditEventSearchResponse, ChangePasswordRequest, CreateManualSalesActivityRequest, CreateManualSalesActivityResponse, CreateNCAPIAccountRequest, CreateUserRequest, DashboardStats, DomainInfo, GetListingActivityRequest, GetListingActivityResponse, GetListingsResponse, GetLTODefaultsResponse, GetNotificationsResponse, InboundEmail, InboundEmailAttachment, Job, JobStatus, LanderDetail, ListAlertsRequest, ListAlertsResponse, ListAlertTypesResponse, ListAutoBidsRequest, ListAutoBidsResponse, ListDomainLeadsRequest, ListDomainLeadsResponse, ListDomainRenewalBlacklistRequest, ListDomainRenewalBlacklistResponse, ListEmailsRequest, ListEmailsResponse, ListJobsRequest, ListJobsResponse, ListLandersRequest, ListLandersResponse, ListNCAftermarketBidsRequest, ListNCAftermarketBidsResponse, ListNCAftermarketSalesRequest, ListNCAftermarketSalesResponse, ListNCAPIAccountsResponse, ListPortfolioRequest, ListPortfolioResponse, ListRulesResponse, ListTagsResponse, ListUsersResponse, ListWatchlistRequest, ListWatchlistResponse, Listing, LoginRequest, NcAuctionBidConfig, NcAuctionExcludedTld, NCAftermarketSale, NCAPIAccount, RemoveWatchlistResponse, SetDomainAutoRenewRequest, SetDomainAutoRenewResponse, SetDomainTagsRequest, TokenResponse, UpdateAlertTypeRequest, UpdateBidConfigRequest, UpdateNCAPIAccountNotesRequest, UpdateNCAPIAccountStatusRequest, UpdateNCAPIAccountTokenRequest, UpdateUserRequest, UploadExcludedTldsResponse, UploadSalesHistoryRequest, UploadSalesHistoryResponse, UploadWatchlistResponse, User, alert, alertAuditLogResponse, alertHistoryResponse, alertType, auditEventSearchResponse, createManualSalesActivityResponse, dashboardStats, domainInfo, getListingActivityResponse, getListingsResponse, getLTODefaultsResponse, getNotificationsResponse, inboundEmail, job, landerDetail, listAlertsResponse, listAlertTypesResponse, listAutoBidsResponse, listDomainLeadsResponse, listDomainRenewalBlacklistResponse, listEmailsResponse, listJobsResponse, listLandersResponse, listNCAftermarketBidsResponse, listNCAftermarketSalesResponse, listNCAPIAccountsResponse, listPortfolioResponse, listRulesResponse, listTagsResponse, listUsersResponse, listWatchlistResponse, listing, ncAftermarketSale, ncAuctionBidConfig, ncAuctionExcludedTld, ncAPIAccount, removeWatchlistResponse, setDomainAutoRenewResponse, tokenResponse, uploadExcludedTldsResponse, uploadSalesHistoryResponse, uploadWatchlistResponse, user } from "./models";
import { DeleteFeatureFlagRequest, FeatureFlag, featureFlag, listFeatureFlagsResponse, UpdateFeatureFlagRequest } from "./models";
import { ContactMigrationRun, contactMigrationRun, StartContactMigrationRequest, StartContactMigrationResponse, startContactMigrationResponse } from "./models";
import { AddWatchlistItemRequest, AddWatchlistItemResponse, NcAuctionWatchlistItem, UpdateWatchlistItemRequest, addWatchlistItemResponse, ncAuctionWatchlistItem } from "./models";
import { FastTransferActivateResponse, fastTransferActivateResponse, FastTransferCaptureResponse, fastTransferCaptureResponse, FastTransferJobDetail, fastTransferJobDetail } from "./models";
import { RenewalJobDetail, RenewalPreview, RenewalRequest, StartRenewalResponse, listRenewalJobsResponse, renewalAccountsResponse, renewalJobDetail, renewalPreview, startRenewalResponse } from "./models";
import { ClickerEvidence, clickerEvidence } from "./models";
import { Moment } from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const REFRESH_TOKEN_STORAGE_KEY = 'parity.refreshToken';
const ACCESS_TOKEN_STORAGE_KEY = 'parity.accessToken';
const TOKEN_REFRESH_LOCK_NAME = 'parity.tokenRefresh';

interface Pagination {
    page: number;
    pageSize: number;
    token?: string;
}

/**
 * Internal axios config flag used to opt-out of the automatic refresh/401
 * retry flow (e.g. for the login/refresh/logout calls themselves). Stored on
 * the config object rather than as an HTTP header so it never hits the wire
 * (and doesn't need to be in CORS Allow-Headers).
 */
type AuthRequestConfig = InternalAxiosRequestConfig & {
    _retry?: boolean;
    _skipAuthRefresh?: boolean;
};

class API {
    instance: Axios
    private accessToken: string | null = null;
    private refreshToken: string | null = null;
    private refreshPromise: Promise<string | null> | null = null;
    private onUnauthorized: (() => void) | null = null;

    constructor() {
        this.instance = axios.create({
            baseURL: API_BASE_URL
        });

        this.refreshToken = this.readStoredRefreshToken();
        this.accessToken = this.readStoredAccessToken();

        window.addEventListener('storage', (event) => {
            if (event.key === REFRESH_TOKEN_STORAGE_KEY) {
                this.refreshToken = event.newValue;
                if (!event.newValue) {
                    this.setAccessToken(null);
                }
            } else if (event.key === ACCESS_TOKEN_STORAGE_KEY) {
                // Another tab refreshed (or logged out); adopt its token
                // without writing it back.
                this.accessToken = event.newValue;
            }
        });

        this.instance.interceptors.request.use((config) => {
            const cfg = config as AuthRequestConfig;
            if (cfg._skipAuthRefresh) return cfg;
            if (this.accessToken) {
                cfg.headers = cfg.headers ?? {};
                cfg.headers['Authorization'] = `Bearer ${this.accessToken}`;
            }
            return cfg;
        });

        this.instance.interceptors.response.use(
            (resp) => resp,
            async (error: AxiosError) => {
                const original = error.config as AuthRequestConfig | undefined;
                if (!original || error.response?.status !== 401 || original._retry || original._skipAuthRefresh) {
                    return Promise.reject(error);
                }

                original._retry = true;
                let newAccess: string | null;
                try {
                    newAccess = await this.refreshAccessToken();
                } catch {
                    // Network error during refresh — don't clear tokens
                    return Promise.reject(error);
                }
                if (!newAccess) {
                    this.clearTokens();
                    this.onUnauthorized?.();
                    return Promise.reject(error);
                }
                original.headers = original.headers ?? {} as any;
                (original.headers as any)['Authorization'] = `Bearer ${newAccess}`;
                return this.instance.request(original);
            }
        );
    }

    setUnauthorizedHandler(handler: (() => void) | null) {
        this.onUnauthorized = handler;
    }

    setAccessToken(token: string | null) {
        this.accessToken = token;
        try {
            if (token) {
                window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
            } else {
                window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
            }
        } catch {
            // ignore storage errors (private mode, quota, etc.)
        }
    }

    private readStoredAccessToken(): string | null {
        try {
            return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
        } catch {
            return this.accessToken;
        }
    }

    getAuthHeaders(): Record<string, string> {
        return this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {};
    }

    private readStoredRefreshToken(): string | null {
        try {
            return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        } catch {
            // Fall back to the in-memory copy when storage is unavailable.
            return this.refreshToken;
        }
    }

    private getLatestRefreshToken(): string | null {
        const stored = this.readStoredRefreshToken();
        this.refreshToken = stored;
        return stored;
    }

    setRefreshToken(token: string | null) {
        this.refreshToken = token;
        try {
            if (token) {
                window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
            } else {
                window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            }
        } catch {
            // ignore storage errors (private mode, quota, etc.)
        }
    }

    getRefreshToken(): string | null {
        return this.getLatestRefreshToken();
    }

    clearTokens() {
        this.setAccessToken(null);
        this.setRefreshToken(null);
    }

    /**
     * Issue a refresh request, coalescing concurrent calls so only one is in
     * flight at a time. Returns the new access token, or null on auth failure.
     * Throws on network errors so callers can distinguish "unauthorized" from
     * "server unreachable".
     */
    private refreshAccessToken(): Promise<string | null> {
        if (this.refreshPromise) return this.refreshPromise;
        if (!this.getLatestRefreshToken()) return Promise.resolve(null);
        const staleAccess = this.accessToken;

        this.refreshPromise = this.withCrossTabRefreshLock(async () => {
            // Another tab may have refreshed while we waited for the lock (or
            // before we saw the 401). Adopt its access token instead of
            // rotating the shared refresh token again.
            const storedAccess = this.readStoredAccessToken();
            if (storedAccess && storedAccess !== staleAccess) {
                this.accessToken = storedAccess;
                return storedAccess;
            }

            const refresh = this.getLatestRefreshToken();
            if (!refresh) return null;
            const refreshed = await this.refreshWithToken(refresh);
            if (refreshed) return refreshed;

            // Another tab may have rotated the token while this request was in
            // flight. Try the newest stored token once before treating the 401
            // as a definitive logout.
            const latest = this.getLatestRefreshToken();
            if (latest && latest !== refresh) {
                return this.refreshWithToken(latest);
            }
            return null;
        }).finally(() => {
            this.refreshPromise = null;
        });

        return this.refreshPromise;
    }

    /**
     * Serialize token refreshes across tabs via the Web Locks API so two tabs
     * never race a rotation (the loser would trip the server's replay
     * detection and revoke every session). Falls back to running unlocked
     * where the API is unavailable.
     */
    private withCrossTabRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
        const locks = (navigator as any)?.locks;
        if (locks?.request) {
            return locks.request(TOKEN_REFRESH_LOCK_NAME, fn);
        }
        return fn();
    }

    private async refreshWithToken(refresh: string): Promise<string | null> {
        try {
            const resp = await this.instance.post('/v1/auth/refresh',
                { refreshToken: refresh },
                { _skipAuthRefresh: true } as AuthRequestConfig
            );
            const parsed = tokenResponse.parse(resp.data);
            this.setAccessToken(parsed.accessToken);
            this.setRefreshToken(parsed.refreshToken);
            return parsed.accessToken;
        } catch (err: any) {
            // Return null (definitive auth failure) only for 401/403.
            // Re-throw everything else (network errors, 5xx) so callers
            // don't clear a still-valid refresh token.
            const status = err?.response?.status;
            if (status === 401 || status === 403) return null;
            throw err;
        }
    }

    async login(req: LoginRequest): Promise<TokenResponse> {
        const resp = await this.instance.post('/v1/auth/login', req,
            { _skipAuthRefresh: true } as AuthRequestConfig);
        const parsed = tokenResponse.parse(resp.data);
        this.setAccessToken(parsed.accessToken);
        this.setRefreshToken(parsed.refreshToken);
        return parsed;
    }

    async logout(): Promise<void> {
        const refresh = this.getLatestRefreshToken();
        this.clearTokens();
        if (!refresh) return;
        try {
            await this.instance.post('/v1/auth/logout', { refreshToken: refresh },
                { _skipAuthRefresh: true } as AuthRequestConfig);
        } catch {
            // best-effort
        }
    }

    async tryRestoreSession(): Promise<User | null> {
        if (!this.getLatestRefreshToken()) return null;
        try {
            // A stored access token (usually minted by another tab) means no
            // rotation is needed to boot; if it turns out to be stale, the 401
            // interceptor refreshes and retries transparently.
            if (!this.accessToken) {
                const token = await this.refreshAccessToken();
                if (!token) {
                    this.clearTokens();
                    return null;
                }
            }
            const resp = await this.instance.get('/v1/auth/me');
            return user.parse(resp.data);
        } catch (err: any) {
            // Only clear tokens on definitive auth rejections (401/403).
            // Everything else (network errors, 5xx, transient 4xx) should
            // preserve tokens so the session can resume.
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                this.clearTokens();
            }
            return null;
        }
    }

    async getCurrentUser(): Promise<User> {
        const resp = await this.instance.get('/v1/auth/me');
        return user.parse(resp.data);
    }

    async changePassword(req: ChangePasswordRequest): Promise<TokenResponse> {
        const resp = await this.instance.post('/v1/auth/change-password', req);
        const parsed = tokenResponse.parse(resp.data);
        this.setAccessToken(parsed.accessToken);
        this.setRefreshToken(parsed.refreshToken);
        return parsed;
    }

    // -------------------- Users (admin) --------------------

    async listUsers(): Promise<ListUsersResponse> {
        const resp = await this.instance.get('/v1/users');
        return listUsersResponse.parse(resp.data);
    }

    async getUser(id: number): Promise<User> {
        const resp = await this.instance.get(`/v1/users/${id}`);
        return user.parse(resp.data);
    }

    async createUser(req: CreateUserRequest): Promise<User> {
        const resp = await this.instance.post('/v1/users', req);
        return user.parse(resp.data);
    }

    async updateUser(id: number, req: UpdateUserRequest): Promise<User> {
        const resp = await this.instance.patch(`/v1/users/${id}`, req);
        return user.parse(resp.data);
    }

    async sendPasswordReset(id: number): Promise<void> {
        await this.instance.post(`/v1/users/${id}/send-password-reset`);
    }

    async resetPassword(token: string, password: string): Promise<void> {
        await this.instance.post('/v1/auth/reset-password',
            { token, password },
            { _skipAuthRefresh: true } as AuthRequestConfig);
    }

    async listRules(): Promise<ListRulesResponse> {
        return listRulesResponse.parse((await this.instance.get('/v1/rules')).data);
    }

    async deactivateRule(id: number): Promise<void> {
        await this.instance.post(`/v1/rules/${id}/deactivate`);
    }

    async dashboardStats(dateStart: Date, dateEnd: Date): Promise<DashboardStats> {
        const resp = await this.instance.get('/v1/dashboard', {
            params: {
                startDate: dayjs(dateStart).utc(true).startOf('day').toISOString(),
                endDate: dayjs(dateEnd).utc(true).endOf('day').toISOString(),
            }
        });
        return dashboardStats.parse(resp.data);
    }
    
    async listLanders(pagination: Pagination, req: ListLandersRequest): Promise<ListLandersResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize};
        if(req.startDate) params.startDate = dayjs(req.startDate).utc(true).startOf('day').toISOString();
        if(req.endDate) params.endDate = dayjs(req.endDate).utc(true).endOf('day').toISOString();
        if(req.search) params.search = req.search;
        if(req.sortBy) params.sortBy = req.sortBy;
        if(req.sortDir) params.sortDir = req.sortDir;
        const resp = await this.instance.get('/v1/landers', {params});
        return listLandersResponse.parse(resp.data);
    }

    async getLander(domain: string, startDate: Date, endDate: Date): Promise<LanderDetail> {
        const resp = await this.instance.get('/v1/landers/' + encodeURIComponent(domain), {
            params: {
                startDate: dayjs(startDate).utc(true).startOf('day').toISOString(),
                endDate: dayjs(endDate).utc(true).endOf('day').toISOString(),
            }
        });
        return landerDetail.parse(resp.data);
    }

    async listPortfolio(pagination: Pagination, req: ListPortfolioRequest): Promise<ListPortfolioResponse> {
        const resp = await this.instance.get('/v1/portfolio', {
            params: {page: pagination.page, pageSize: pagination.pageSize, search: req?.search}
        });
        return listPortfolioResponse.parse(resp.data);
    }

    getListDomainLeadsParams(req: ListDomainLeadsRequest) {
        return {search: req?.search, account: req?.account, pickStatus: req?.pickStatus?.join(','), portfolio: req.portfolio, picksToSendOnly: req.picksToSendOnly,
            badNS: req.filterBadNS, clientHold: req.clientHold ? 'true': undefined, tags: req.tags?.join(','),
            needsPricing: req.needsPricing};
    }

    async getJob(jobId: number): Promise<Job> {
        const resp = await this.instance.get('/v1/jobs/' + encodeURIComponent(jobId));
        return job.parse(resp.data);
    }

    async getDomain(domain: string): Promise<DomainInfo|undefined> {
        try {
            const resp = await this.instance.get('/v1/domain/' + encodeURIComponent(domain));
            return domainInfo.parse(resp.data);
        }
        catch(e) {
            if(e instanceof AxiosError) {
                if(e.response?.status == 404) {
                    return undefined;
                }
            }
            else {
                throw e;
            }
        }
    }

    async listJobs(pagination: Pagination, req: ListJobsRequest): Promise<ListJobsResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize, type: req?.type?.join(",")};
        if(req?.status) {
            params.status = req.status;
        }
        const resp = await this.instance.get('/v1/jobs', {
            params
        });
        return listJobsResponse.parse(resp.data);
    }

    async listTags(): Promise<ListTagsResponse> {
        const resp = await this.instance.get('/v1/tags');
        return listTagsResponse.parse(resp.data);
    }

    async setDomainTags(domain: string, req: SetDomainTagsRequest) {
        await this.instance.post('v1/domain/' + encodeURIComponent(domain) + '/tags', req);
    }

    async getEmail(id: number): Promise<InboundEmail> {
        const resp = await this.instance.get('/v1/inboundEmails/' + encodeURIComponent(id));
        return inboundEmail.parse(resp.data);
    }

    async deleteEmailTag(emailId: number, tag: string): Promise<void> {
        await this.instance.delete('/v1/inboundEmails/' + encodeURIComponent(emailId) + '/tags/' + encodeURIComponent(tag));
    }

    async listEmails(pagination: Pagination, req: ListEmailsRequest): Promise<ListEmailsResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize};

        if(req.tag) {
            params.tag = req.tag;
        }
        if(req.search) params.search = req.search;
        if(req.disposition) params.disposition = req.disposition;

        const resp = await this.instance.get('/v1/inboundEmails', {params});
        return listEmailsResponse.parse(resp.data);
    }

    async getListings(
        pagination: Pagination,
        filters?: { ltoActive?: boolean; domain?: string; venue?: string; status?: string },
    ): Promise<GetListingsResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize};
        if (filters?.ltoActive) params.ltoActive = 'true';
        if (filters?.domain) params.domain = filters.domain;
        if (filters?.venue) params.venue = filters.venue;
        if (filters?.status) params.status = filters.status;
        const resp = await this.instance.get('/v1/sales/listings', {params});
        return getListingsResponse.parse(resp.data);
    }

    async getListingActivity(pagination: Pagination, req?: GetListingActivityRequest): Promise<GetListingActivityResponse> {
        var params: any = {page: pagination.page, pageSize: pagination.pageSize};
        if(req) {
            params = {...params, ...req};
        }
        const resp = await this.instance.get('/v1/sales/activity', {params});
        return getListingActivityResponse.parse(resp.data);
    }

    async getLTODefaults(pagination: Pagination): Promise<GetLTODefaultsResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize};
        const resp = await this.instance.get('/v1/sales/lto-defaults', {params});
        return getLTODefaultsResponse.parse(resp.data);
    }

    async listDomainLeads(pagination: Pagination, req: ListDomainLeadsRequest): Promise<ListDomainLeadsResponse> {
        const params: any = {...this.getListDomainLeadsParams(req), pageSize: pagination.pageSize};
        if(pagination.token) {
            params.token = pagination.token;
        }
        const resp = await this.instance.post('/v1/domainLeads', {
            domains: req.domains,
            tagAll: req.tagAll,
        }, {
            params,
        });
        return listDomainLeadsResponse.parse(resp.data);
    }

    async listNCAftermarketSales(req: ListNCAftermarketSalesRequest): Promise<ListNCAftermarketSalesResponse> {
        const params: any = {};
        if (req.pageSize !== undefined) params.pageSize = req.pageSize;
        if (req.cursor) params.cursor = req.cursor;
        if (req.orderBy) params.orderBy = req.orderBy;
        if (req.direction) params.direction = req.direction;
        if (req.name) params.name = req.name;
        if (req.tld) params.tld = req.tld;
        if (req.keywords) params.keywords = req.keywords;
        if (req.price) params.price = req.price;
        if (req.nameLength) params.nameLength = req.nameLength;
        if (req.bidCount) params.bidCount = req.bidCount;
        if (req.startDate) params.startDate = req.startDate;
        if (req.endDate) params.endDate = req.endDate;
        if (req.noHyphens) params.noHyphens = 'true';
        if (req.noNumbers) params.noNumbers = 'true';
        if (req.onlyNumbers) params.onlyNumbers = 'true';
        const resp = await this.instance.get('/v1/ncAftermarket/sales', { params });
        return listNCAftermarketSalesResponse.parse(resp.data);
    }

    async getNCAftermarketSale(id: string): Promise<NCAftermarketSale> {
        const resp = await this.instance.get('/v1/ncAftermarket/sales/' + encodeURIComponent(id));
        return ncAftermarketSale.parse(resp.data);
    }

    async listNCAftermarketBids(req: ListNCAftermarketBidsRequest): Promise<ListNCAftermarketBidsResponse> {
        const params: any = {};
        if (req.page !== undefined) params.page = req.page;
        if (req.pageSize !== undefined) params.pageSize = req.pageSize;
        if (req.sale) params.sale = req.sale;
        const resp = await this.instance.get('/v1/ncAftermarket/bids', { params });
        return listNCAftermarketBidsResponse.parse(resp.data);
    }

    async listDomainRenewalBlacklist(pagination: Pagination, req: ListDomainRenewalBlacklistRequest): Promise<ListDomainRenewalBlacklistResponse> {
        const params: any = {page: pagination.page, pageSize: pagination.pageSize};
        if(req.search) params['search'] = req.search;
        return listDomainRenewalBlacklistResponse.parse((await this.instance.post('/v1/renewalBlacklist/search', {
            domains: req.domains,
        }, {params})).data);
    }
    
    async batchAddDomainRenewalBlacklist(formData: FormData): Promise<void> {
        await this.instance.post('/v1/renewalBlacklist', formData, {
            headers: {"Content-Type": 'multipart/form-data'},
        });
    }

    async batchDeleteDomainRenewalBlacklist(formData: FormData): Promise<void> {
        await this.instance.delete('/v1/renewalBlacklist', {
            headers: {"Content-Type": 'multipart/form-data'},
            data: formData
        });
    }

    async getNotifications(): Promise<GetNotificationsResponse> {
        return getNotificationsResponse.parse((await this.instance.get('/v1/notifications')).data);
    }

    downloadDomainLeads(req: ListDomainLeadsRequest): string {
        const params = {...this.getListDomainLeadsParams(req), download: true};
        return API_BASE_URL + '/v1/domainLeads?' + Object.entries(params).map(([k,v]) => k + "=" + encodeURIComponent(v||'')).join("&");
    }

    async uploadSalesHistory(req: UploadSalesHistoryRequest): Promise<UploadSalesHistoryResponse> {
        const resp = await this.instance.post('/v1/sales/loadHistoricalData', {
            file: req.file,
        }, {
            headers: {
                "Content-Type": "multipart/form-data"
            },
            params: {
                fileType: req.fileType,
                account: req.account,
            }
        });
        return uploadSalesHistoryResponse.parse(resp.data);
    }

    getAttachmentDownloadUrl(attachment: InboundEmailAttachment) {
        return API_BASE_URL + '/v1/inboundEmailAttachments/' + encodeURIComponent(attachment.id) + '/download';
    }

    // NC API Accounts
    async listNCAPIAccounts(): Promise<ListNCAPIAccountsResponse> {
        const resp = await this.instance.get('/v1/nc-accounts');
        return listNCAPIAccountsResponse.parse(resp.data);
    }

    async getNCAPIAccount(id: number): Promise<NCAPIAccount> {
        const resp = await this.instance.get(`/v1/nc-accounts/${id}`);
        return ncAPIAccount.parse(resp.data);
    }

    async createNCAPIAccount(req: CreateNCAPIAccountRequest): Promise<NCAPIAccount> {
        const resp = await this.instance.post('/v1/nc-accounts', req);
        return ncAPIAccount.parse(resp.data);
    }

    async updateNCAPIAccountToken(id: number, req: UpdateNCAPIAccountTokenRequest): Promise<NCAPIAccount> {
        const resp = await this.instance.post(`/v1/nc-accounts/${id}/update-token`, req);
        return ncAPIAccount.parse(resp.data);
    }

    async refreshNCAPIAccountToken(id: number): Promise<NCAPIAccount> {
        const resp = await this.instance.post(`/v1/nc-accounts/${id}/refresh-token`);
        return ncAPIAccount.parse(resp.data);
    }

    async updateNCAPIAccountStatus(id: number, req: UpdateNCAPIAccountStatusRequest): Promise<void> {
        await this.instance.patch(`/v1/nc-accounts/${id}/status`, req);
    }

    async updateNCAPIAccountNotes(id: number, req: UpdateNCAPIAccountNotesRequest): Promise<void> {
        await this.instance.patch(`/v1/nc-accounts/${id}/notes`, req);
    }

    async searchAuditEvents(params: {
        startDate?: Date;
        endDate?: Date;
        eventTypes: string[];
        account?: string;
        domain?: string;
        page?: number;
        pageSize?: number;
    }): Promise<AuditEventSearchResponse> {
        const resp = await this.instance.get('/v1/auditEvents/search', {
            params: {
                startDate: params.startDate?.toISOString(),
                endDate: params.endDate?.toISOString(),
                eventTypes: params.eventTypes.join(','),
                account: params.account,
                domain: params.domain,
                page: params.page || 1,
                pageSize: params.pageSize || 10,
            }
        });
        return auditEventSearchResponse.parse(resp.data);
    }

    getAuditEventsExportUrl(params: {
        startDate?: Date;
        endDate?: Date;
        eventTypes: string[];
        account?: string;
        domain?: string;
    }): string {
        const queryParams: Record<string, string> = {
            eventTypes: params.eventTypes.join(','),
        };
        if (params.startDate) {
            queryParams.startDate = params.startDate.toISOString();
        }
        if (params.endDate) {
            queryParams.endDate = params.endDate.toISOString();
        }
        if (params.account) {
            queryParams.account = params.account;
        }
        if (params.domain) {
            queryParams.domain = params.domain;
        }
        return API_BASE_URL + '/v1/auditEvents/export?' + Object.entries(queryParams)
            .map(([k, v]) => k + '=' + encodeURIComponent(v))
            .join('&');
    }

    async setDomainAutoRenew(domain: string, req: SetDomainAutoRenewRequest): Promise<SetDomainAutoRenewResponse> {
        const resp = await this.instance.post('/v1/domain/' + encodeURIComponent(domain) + '/autorenew', req);
        return setDomainAutoRenewResponse.parse(resp.data);
    }

    async createManualSalesActivity(req: CreateManualSalesActivityRequest): Promise<CreateManualSalesActivityResponse> {
        const resp = await this.instance.post('/v1/sales/activity', req);
        return createManualSalesActivityResponse.parse(resp.data);
    }

    // Alerts
    async listAlerts(pagination: Pagination, req?: ListAlertsRequest): Promise<ListAlertsResponse> {
        const params: any = { page: pagination.page, pageSize: pagination.pageSize };
        if (req?.status) params.status = req.status;
        if (req?.alertTypeId) params.alertTypeId = req.alertTypeId;
        if (req?.search) params.search = req.search;
        const resp = await this.instance.get('/v1/alerts', { params });
        return listAlertsResponse.parse(resp.data);
    }

    async getAlert(id: number): Promise<Alert> {
        const resp = await this.instance.get(`/v1/alerts/${id}`);
        return alert.parse(resp.data);
    }

    async updateAlertNotes(id: number, notes: string): Promise<Alert> {
        const resp = await this.instance.patch(`/v1/alerts/${id}/notes`, { notes });
        return alert.parse(resp.data);
    }

    async dismissAlert(id: number): Promise<Alert> {
        const resp = await this.instance.patch(`/v1/alerts/${id}/dismiss`);
        return alert.parse(resp.data);
    }

    async getAlertHistory(id: number): Promise<AlertHistoryResponse> {
        const resp = await this.instance.get(`/v1/alerts/${id}/history`);
        return alertHistoryResponse.parse(resp.data);
    }

    async getAlertAuditLog(id: number): Promise<AlertAuditLogResponse> {
        const resp = await this.instance.get(`/v1/alerts/${id}/audit-log`);
        return alertAuditLogResponse.parse(resp.data);
    }

    // Autorenew Review Queue
    async listAutoRenewReview(
        pagination: Pagination,
        req?: ListAutoRenewReviewRequest,
    ): Promise<ListAutoRenewReviewResponse> {
        const params: any = { page: pagination.page, pageSize: pagination.pageSize };
        if (req?.status) params.status = req.status;
        if (req?.sortBy) params.sortBy = req.sortBy;
        if (req?.sortDir) params.sortDir = req.sortDir;
        const resp = await this.instance.get('/v1/autorenew-review', { params });
        return listAutoRenewReviewResponse.parse(resp.data);
    }

    async reenableAutoRenewReview(id: number): Promise<AutoRenewReviewEntry> {
        const resp = await this.instance.post(`/v1/autorenew-review/${id}/reenable`);
        return autoRenewReviewEntry.parse(resp.data);
    }

    async keepOffAutoRenewReview(id: number): Promise<AutoRenewReviewEntry> {
        const resp = await this.instance.post(`/v1/autorenew-review/${id}/keep-off`);
        return autoRenewReviewEntry.parse(resp.data);
    }

    // Alert Types
    async listAlertTypes(): Promise<ListAlertTypesResponse> {
        const resp = await this.instance.get('/v1/alert-types');
        return listAlertTypesResponse.parse(resp.data);
    }

    async updateAlertType(id: number, req: UpdateAlertTypeRequest): Promise<AlertType> {
        const resp = await this.instance.patch(`/v1/alert-types/${id}`, req);
        return alertType.parse(resp.data);
    }

    // NC Auto-Bidder

    async listWatchlist(req: ListWatchlistRequest): Promise<ListWatchlistResponse> {
        const params: any = {};
        if (req.page !== undefined) params.page = req.page;
        if (req.pageSize !== undefined) params.pageSize = req.pageSize;
        if (req.status) params.status = req.status;
        if (req.statusNot) params.statusNot = req.statusNot;
        if (req.domain) params.domain = req.domain;
        if (req.bidMode) params.bidMode = req.bidMode;
        const resp = await this.instance.get('/v1/ncAutoBidder/watchlist', { params });
        return listWatchlistResponse.parse(resp.data);
    }

    async addWatchlistItem(req: AddWatchlistItemRequest): Promise<AddWatchlistItemResponse> {
        const resp = await this.instance.post('/v1/ncAutoBidder/watchlist', req);
        return addWatchlistItemResponse.parse(resp.data);
    }

    async updateWatchlistItem(id: number, req: UpdateWatchlistItemRequest): Promise<NcAuctionWatchlistItem> {
        const resp = await this.instance.patch(`/v1/ncAutoBidder/watchlist/${id}`, req);
        return ncAuctionWatchlistItem.parse(resp.data);
    }

    async uploadWatchlist(file: File): Promise<UploadWatchlistResponse> {
        const form = new FormData();
        form.append('file', file);
        const resp = await this.instance.post('/v1/ncAutoBidder/watchlist/upload', form);
        return uploadWatchlistResponse.parse(resp.data);
    }

    async removeWatchlistItem(id: number): Promise<void> {
        await this.instance.delete(`/v1/ncAutoBidder/watchlist/${id}`);
    }

    async removeWatchlist(file: File): Promise<RemoveWatchlistResponse> {
        const form = new FormData();
        form.append('file', file);
        const resp = await this.instance.post('/v1/ncAutoBidder/watchlist/remove', form);
        return removeWatchlistResponse.parse(resp.data);
    }

    async listAutoBids(req: ListAutoBidsRequest): Promise<ListAutoBidsResponse> {
        const params: any = {};
        if (req.page !== undefined) params.page = req.page;
        if (req.pageSize !== undefined) params.pageSize = req.pageSize;
        if (req.status) params.status = req.status;
        if (req.domain) params.domain = req.domain;
        if (req.dryRun) params.dryRun = req.dryRun;
        const resp = await this.instance.get('/v1/ncAutoBidder/bids', { params });
        return listAutoBidsResponse.parse(resp.data);
    }

    async listExcludedTlds(): Promise<NcAuctionExcludedTld[]> {
        const resp = await this.instance.get('/v1/ncAutoBidder/excludedTlds');
        return z.array(ncAuctionExcludedTld).parse(resp.data);
    }

    async uploadExcludedTlds(file: File): Promise<UploadExcludedTldsResponse> {
        const form = new FormData();
        form.append('file', file);
        const resp = await this.instance.post('/v1/ncAutoBidder/excludedTlds/upload', form);
        return uploadExcludedTldsResponse.parse(resp.data);
    }

    async addExcludedTld(tld: string): Promise<NcAuctionExcludedTld> {
        const resp = await this.instance.post('/v1/ncAutoBidder/excludedTlds', { tld });
        return ncAuctionExcludedTld.parse(resp.data);
    }

    async deleteExcludedTld(id: number): Promise<void> {
        await this.instance.delete(`/v1/ncAutoBidder/excludedTlds/${id}`);
    }

    async getAutoBidderConfig(): Promise<NcAuctionBidConfig> {
        const resp = await this.instance.get('/v1/ncAutoBidder/config');
        return ncAuctionBidConfig.parse(resp.data);
    }

    async updateAutoBidderConfig(req: UpdateBidConfigRequest): Promise<NcAuctionBidConfig> {
        const resp = await this.instance.patch('/v1/ncAutoBidder/config', req);
        return ncAuctionBidConfig.parse(resp.data);
    }

    // Feature flags (kill switches)

    async listFeatureFlags(): Promise<FeatureFlag[]> {
        const resp = await this.instance.get('/v1/feature-flags');
        return listFeatureFlagsResponse.parse(resp.data);
    }

    async updateFeatureFlag(req: UpdateFeatureFlagRequest): Promise<FeatureFlag> {
        const resp = await this.instance.patch('/v1/feature-flags', req);
        return featureFlag.parse(resp.data);
    }

    async deleteFeatureFlagOverride(req: DeleteFeatureFlagRequest): Promise<void> {
        await this.instance.delete('/v1/feature-flags', { data: req });
    }

    // Contact migration (fast transfer opt-in)

    async startContactMigration(req: StartContactMigrationRequest): Promise<StartContactMigrationResponse> {
        const resp = await this.instance.post('/v1/admin/contactMigration', req);
        return startContactMigrationResponse.parse(resp.data);
    }

    async getContactMigrationRun(workflowId: string): Promise<ContactMigrationRun> {
        const resp = await this.instance.get(`/v1/admin/contactMigration/${encodeURIComponent(workflowId)}`);
        return contactMigrationRun.parse(resp.data);
    }

    // Fast transfer admin activation

    async getFastTransferJob(domain: string): Promise<FastTransferJobDetail> {
        const resp = await this.instance.get('/v1/admin/fastTransfer/jobs', { params: { domain } });
        return fastTransferJobDetail.parse(resp.data);
    }

    async activateFastTransfer(domain: string): Promise<FastTransferActivateResponse> {
        const resp = await this.instance.post('/v1/admin/fastTransfer/activate', { domain });
        return fastTransferActivateResponse.parse(resp.data);
    }

    async captureFastTransferTerms(jobId: number): Promise<FastTransferCaptureResponse> {
        const resp = await this.instance.post(`/v1/admin/fastTransfer/jobs/${jobId}/capture`);
        return fastTransferCaptureResponse.parse(resp.data);
    }

    async acceptFastTransferTerms(jobId: number): Promise<FastTransferJobDetail> {
        const resp = await this.instance.post(`/v1/admin/fastTransfer/jobs/${jobId}/accept`);
        return fastTransferJobDetail.parse({ job: resp.data.job });
    }

    async approveFastTransferTerms(versionId: number): Promise<void> {
        await this.instance.post(`/v1/fast-transfer/terms/${versionId}/approve`);
    }

    // Bulk domain renewal

    async listRenewalAccounts(): Promise<string[]> {
        const resp = await this.instance.get('/v1/renewals/accounts');
        return renewalAccountsResponse.parse(resp.data).accounts;
    }

    async previewRenewal(req: RenewalRequest): Promise<RenewalPreview> {
        const resp = await this.instance.post('/v1/renewals/preview', req);
        return renewalPreview.parse(resp.data);
    }

    async startRenewal(req: RenewalRequest): Promise<StartRenewalResponse> {
        const resp = await this.instance.post('/v1/renewals', req);
        return startRenewalResponse.parse(resp.data);
    }

    async listRenewalJobs(page: number, pageSize: number) {
        const resp = await this.instance.get('/v1/renewals', { params: { page, pageSize } });
        return listRenewalJobsResponse.parse(resp.data);
    }

    async getRenewalJob(id: number): Promise<RenewalJobDetail> {
        const resp = await this.instance.get(`/v1/renewals/${id}`);
        return renewalJobDetail.parse(resp.data);
    }

    // Clicker evidence (screenshot + DOM snapshot of a registrar-page run)

    async getClickerEvidence(id: number): Promise<ClickerEvidence> {
        const resp = await this.instance.get(`/v1/clicker-evidence/${id}`);
        return clickerEvidence.parse(resp.data);
    }

    async getClickerEvidenceScreenshot(id: number): Promise<Blob> {
        const resp = await this.instance.get(`/v1/clicker-evidence/${id}/screenshot`, { responseType: 'blob' });
        return resp.data;
    }

    async getClickerEvidenceDom(id: number): Promise<string> {
        const resp = await this.instance.get(`/v1/clicker-evidence/${id}/dom`, {
            responseType: 'text',
            transformResponse: [(data) => data],
        });
        return String(resp.data);
    }
}

const APIClient = new API();

export default APIClient;
